import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Card, Input, useToast, v } from '@kss/ui';
import { useAuth, updateProfile } from '@kss/auth';
import { Camera, Trash2 } from 'lucide-react';

// 20 KB — stored as base64 in redux-persist. Hard cap so the persisted
// auth slice stays well under the localStorage 5 MB ceiling and the JSON
// payload sent to any future profile-update endpoint stays cheap.
const MAX_AVATAR_BYTES = 20 * 1024;

/**
 * Lets a signed-in customer edit profile basics + avatar.
 *
 * Avatar is uploaded as base64 directly into the auth slice — once an
 * `assetService.upload` endpoint exists, this should be replaced with a
 * real multipart upload that returns a URL.
 */
export default function AccountProfile() {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  // Phone wasn't being initialised from `user.phone`, AND the
  // submit handler used to drop it on the floor — so even
  // returning customers lost their saved phone every page load
  // and a fresh edit silently failed to persist.
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  /* Name and email had NO validation — a blank name or `not-an-email`
     saved silently. Phone had a bare length check that passed
     "0000000000"; the shared rule also enforces the 6-9 leading digit. */
  const profileErrors: Record<string, string | null> = {
    name: v.runRules(name, [v.required('Full name'), v.maxLength(120, 'Full name'), ...v.safeText('Full name')]),
    email: v.runRules(email, [v.required('Email'), v.email()]),
    phone: v.runRules(phone, [v.mobile({ label: 'Phone' })]),
  };
  const profErr = (k: string) => (touched[k] ? profileErrors[k] ?? undefined : undefined);
  const markP = (k: string) => () => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const profileValid = !Object.values(profileErrors).some(Boolean);

  function pickAvatar() {
    fileRef.current?.click();
  }

  function onAvatarFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Invalid file', 'Pick an image (PNG, JPG, WebP).');
      return;
    }
    if (f.size > MAX_AVATAR_BYTES) {
      toast.error('Image too large', `Max ${Math.round(MAX_AVATAR_BYTES / 1024)}KB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result ?? '');
      dispatch(updateProfile({ avatar: dataUrl }));
      toast.success('Avatar updated', f.name);
    };
    reader.onerror = () => toast.error('Read failed', 'Could not read the image.');
    reader.readAsDataURL(f);
  }

  function clearAvatar() {
    dispatch(updateProfile({ avatar: undefined }));
    toast.info('Avatar removed');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    // TODO: call `authService.updateMyProfile(...)` once the endpoint
    //       exists — see BACKEND_AGENT_PROMPTS Item 6.1. Until then,
    //       persistence is just the redux-persist localStorage layer
    //       (which survives refresh on the same browser). Phone is
    //       included now that `User.phone` is typed; previously it
    //       was collected and silently discarded.
    setTimeout(() => {
      dispatch(updateProfile({ name, email, phone: phone.trim() || undefined }));
      setSubmitting(false);
      toast.success('Profile updated', 'Your details were saved.');
    }, 250);
  }

  const initial = (name || user?.name || '?').charAt(0).toUpperCase();

  return (
    <div className="grid gap-3">
      <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">Profile</h1>
      <Card className="p-5">
        <div className="mb-5 flex items-center gap-4">
          <div className="relative">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-[var(--color-border)] bg-[var(--color-bg-app)] text-2xl font-semibold text-[var(--color-text-secondary)]">
              {user?.avatar ? (
                <img src={user.avatar} alt="" className="h-full w-full object-cover" />
              ) : (
                <span>{initial}</span>
              )}
            </div>
            <button
              type="button"
              onClick={pickAvatar}
              aria-label="Change avatar"
              className="absolute -right-1 -bottom-1 inline-flex h-7 w-7 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow hover:bg-[var(--color-bg-card-hover)]"
            >
              <Camera className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-[var(--color-text-primary)]">{user?.name}</p>
            <p className="text-xs text-[var(--color-text-secondary)]">{user?.email}</p>
            <div className="mt-1 flex gap-2">
              <Button size="sm" variant="outline" onClick={pickAvatar}>
                Upload photo
              </Button>
              {user?.avatar && (
                <button
                  type="button"
                  onClick={clearAvatar}
                  className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card-hover)]"
                >
                  <Trash2 className="h-3 w-3" />
                  Remove
                </button>
              )}
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={onAvatarFile}
            hidden
          />
        </div>

        <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-2">
          <Input label="Full name" value={name} onChange={(e) => setName(e.target.value)} onBlur={markP("name")} error={profErr("name")} placeholder="Jane Doe" />
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={markP("email")} error={profErr("email")} placeholder="you@company.com" />
          <Input
            label="Phone"
            value={phone}
            /* Numeric-only sanitiser at input boundary; matches
               CheckoutPage / AccountAddresses convention.
               Bug ref: AI Agent Issue #2. */
            onChange={(e) => setPhone(e.target.value.replace(/\D+/g, '').slice(0, 15))}
            inputMode="numeric"
            pattern="\d{10,15}"
            maxLength={15}
            placeholder="10-digit mobile"
            onBlur={markP('phone')}
            error={profErr('phone')}
          />
          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" disabled={submitting || !profileValid}>
              {submitting ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
